/**
 * Factory for the consolidated-settings redux feature folders.
 *
 * `siteSettings`, `siteGroupSettings`, and `customerSettings` were
 * three byte-for-byte-similar folders with `siteId` /
 * `siteGroupId` / `customerId` swapped — load/save thunks, the
 * cache shape, the slice reducer, the selector wiring, and the
 * fetch-on-mount hook are all entity-agnostic.  This factory
 * produces all of them from a single config object so the only
 * per-entity code is the entity-specific names + section keys
 * declared in each folder's `index.js`.
 *
 * Public contract preserved by callers:
 *
 *   const feature = createSettingsFeature({
 *     name: 'customerSettings',          // slice + thunk-prefix name
 *     idKey: 'customerId',               // arg + meta key
 *     api: { fetch, patch },             // raw API helpers
 *   });
 *
 *   feature.actions.{load, save, invalidate}
 *   feature.reducer
 *   feature.selectors.{settings, status, saveStatus, error}
 *   feature.selectSection(key)           // memoized per-section selector
 *   feature.useSettings({ <idKey>, autoLoad? })
 *   feature.useSection(key)              // memoized per-section hook
 *
 * Section selectors / hooks are returned from `selectSection` /
 * `useSection` factories, each wrapped in `memoizeByKey` (the shared
 * selector-cache convention — see `utils/memoizeByKey.js`) so every
 * `feature.selectSection('general')` call returns the same function
 * instance — required for `useSelector` referential stability.
 *
 * Each per-entity folder consumes this with explicit re-exports so
 * the public names (`selectSiteGeneral`, `useCustomerInstructions`,
 * …) stay greppable rather than getting auto-generated from
 * snake_case section keys.
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { memoizeByKey } from '@sophocles/utils/memoizeByKey';

const defaultKeyFor = id => String(id);

// `keyFor` may be overridden for features that accept a sentinel id
// (e.g. `userSettings` treats `null` as the requesting user's `/me/`
// cache slot and keys it under `'self'` so callers that don't know
// their own user id can still read).
export function createSettingsFeature({ name, idKey, api, keyFor = defaultKeyFor }) {
  // --- thunks ------------------------------------------------------

  const load = createAsyncThunk(`${name}/load`, async (arg, { rejectWithValue }) => {
    try {
      return await api.fetch(arg[idKey]);
    } catch (err) {
      return rejectWithValue({ message: err.message, status: err.response?.status });
    }
  });

  // `payload` is a sectioned partial — only sections the caller wants
  // to change.  Server returns the full post-save snapshot which the
  // reducer caches in place of the prior value.
  const save = createAsyncThunk(`${name}/save`, async (arg, { rejectWithValue }) => {
    try {
      return await api.patch(arg[idKey], arg.payload);
    } catch (err) {
      return rejectWithValue({
        message: err.message,
        status: err.response?.status,
        body: err.response?.data,
      });
    }
  });

  // --- slice -------------------------------------------------------

  const initialState = {
    settingsById: {},
    statusById: {}, // 'idle' | 'loading' | 'succeeded' | 'failed'
    errorById: {},
    saveStatusById: {},
  };

  const slice = createSlice({
    name,
    initialState,
    reducers: {
      invalidate: (state, action) => {
        const key = keyFor(action.payload?.[idKey]);
        delete state.settingsById[key];
        state.statusById[key] = 'idle';
      },
      // Non-destructive variant of invalidate: keeps the cached data in place
      // (so the view keeps rendering) but resets status to 'idle' so the
      // useSettings fetch-on-mount effect triggers a background refetch.
      // Use this whenever the caller wants fresh data without blanking the page.
      markStale: (state, action) => {
        const key = keyFor(action.payload?.[idKey]);
        if (state.settingsById[key] !== undefined) {
          state.statusById[key] = 'idle';
        }
      },
    },
    extraReducers: builder => {
      builder
        .addCase(load.pending, (state, action) => {
          const key = keyFor(action.meta.arg?.[idKey]);
          state.statusById[key] = 'loading';
          state.errorById[key] = null;
        })
        .addCase(load.fulfilled, (state, action) => {
          const key = keyFor(action.meta.arg?.[idKey]);
          state.settingsById[key] = action.payload;
          state.statusById[key] = 'succeeded';
        })
        .addCase(load.rejected, (state, action) => {
          const key = keyFor(action.meta.arg?.[idKey]);
          state.statusById[key] = 'failed';
          state.errorById[key] = action.payload || { message: action.error?.message };
        })
        .addCase(save.pending, (state, action) => {
          const key = keyFor(action.meta.arg?.[idKey]);
          state.saveStatusById[key] = 'loading';
        })
        .addCase(save.fulfilled, (state, action) => {
          const key = keyFor(action.meta.arg?.[idKey]);
          state.settingsById[key] = action.payload;
          state.saveStatusById[key] = 'succeeded';
        })
        .addCase(save.rejected, (state, action) => {
          const key = keyFor(action.meta.arg?.[idKey]);
          state.saveStatusById[key] = 'failed';
          state.errorById[key] = action.payload || { message: action.error?.message };
        });
    },
  });

  // --- selectors ---------------------------------------------------

  const sliceState = state => state[name];

  const selectSettings = (state, id) => sliceState(state).settingsById[keyFor(id)] ?? null;
  const selectStatus = (state, id) => sliceState(state).statusById[keyFor(id)] ?? 'idle';
  const selectSaveStatus = (state, id) => sliceState(state).saveStatusById[keyFor(id)] ?? 'idle';
  const selectError = (state, id) => sliceState(state).errorById[keyFor(id)] ?? null;

  // Memoize per-section selectors so repeated `selectSection('general')`
  // calls return the same function instance.  `useSelector` compares
  // returned values with `===`, but it also caches the selector itself
  // — handing it a fresh function on every render would defeat
  // section-level identity tracking elsewhere.
  const selectSection = memoizeByKey(section => (state, id) => {
    const settings = selectSettings(state, id);
    return settings ? settings[section] : null;
  });

  // --- hooks -------------------------------------------------------

  const useSettings = (arg = {}) => {
    const id = arg[idKey];
    const autoLoad = arg.autoLoad !== false;
    const dispatch = useDispatch();
    const settings = useSelector(state => selectSettings(state, id));
    const status = useSelector(state => selectStatus(state, id));
    const saveStatus = useSelector(state => selectSaveStatus(state, id));
    const error = useSelector(state => selectError(state, id));

    useEffect(() => {
      // `id === undefined` short-circuits routes that haven't resolved
      // their param yet.  `null` is a valid sentinel (userSettings uses
      // it for the /me/ slot, keyed under 'self' via its custom keyFor),
      // so we do NOT treat it the same as missing.
      if (!autoLoad || id === undefined) return;
      // Only auto-fetch on `idle` — never on `failed`, otherwise a
      // persistent server error loops indefinitely.  Callers can
      // retry manually via the returned `reload()`.
      if (status === 'idle') {
        dispatch(load({ [idKey]: id }));
      }
    }, [autoLoad, dispatch, id, status]);

    const reload = () => dispatch(load({ [idKey]: id }));
    const savePayload = payload => dispatch(save({ [idKey]: id, payload })).unwrap();

    return { settings, status, saveStatus, error, reload, save: savePayload };
  };

  // Single-section convenience hook factory — pulls one slice plus
  // load status, no save handle.  Use in components that only read.
  //
  // Each section gets its own hook with a `use`-prefixed name so
  // React's Rules-of-Hooks linter recognizes it as a hook at the
  // call site (anonymous arrows confuse it).
  const useSection = memoizeByKey(section => {
    const sel = selectSection(section);
    const useSectionHook = id => {
      const dispatch = useDispatch();
      const value = useSelector(state => sel(state, id));
      const status = useSelector(state => selectStatus(state, id));
      useEffect(() => {
        // See useSettings — `null` is a valid sentinel id, only
        // `undefined` short-circuits.
        if (id === undefined) return;
        if (status === 'idle') dispatch(load({ [idKey]: id }));
      }, [dispatch, id, status]);
      return { value, status };
    };
    Object.defineProperty(useSectionHook, 'name', {
      value: `use${section.replace(/(^|_)([a-z])/g, (_, __, c) => c.toUpperCase())}`,
    });
    return useSectionHook;
  });

  return {
    actions: {
      load,
      save,
      invalidate: slice.actions.invalidate,
      markStale: slice.actions.markStale,
    },
    reducer: slice.reducer,
    selectors: {
      settings: selectSettings,
      status: selectStatus,
      saveStatus: selectSaveStatus,
      error: selectError,
    },
    selectSection,
    useSettings,
    useSection,
  };
}
